import dayjs from 'dayjs';

/**
 * Backend datetime contract (UK-only deployment)
 * -----------------------------------------------
 * Django settings: ``TIME_ZONE = "Europe/London"`` and ``USE_TZ = True``.
 * DRF settings: ``DATETIME_FORMAT = "%d-%m-%Y %H:%M:%S.%fZ"``.
 *
 * The trailing ``Z`` in the format string is a LITERAL CHARACTER, not a
 * UTC marker. Django activates ``Europe/London`` for every request, so
 * ``enforce_timezone`` converts the stored UTC value to London local time
 * BEFORE ``strftime`` renders it. The resulting string therefore reflects
 * London local time even though it carries a misleading ``Z`` suffix.
 *
 * Example: a task due at 09:00 London time on 2026-05-20
 *   - DB column: 2026-05-20T08:00:00+00:00 (UTC)
 *   - Wire string: "20-05-2026 09:00:00.000000Z"  ← 09:00 is LONDON, not UTC.
 *
 * Any parser that respects the ``Z`` as UTC will produce 10:00 BST in
 * summer (BST = UTC+1), 09:00 in winter (GMT = UTC). That asymmetric
 * shift was the 2026-05 "tasks showing 1 hour ahead" report.
 */

/**
 * Format a local Date object as ``YYYY-MM-DDTHH:mm`` for use as the value
 * of an ``<input type="datetime-local">`` widget.
 *
 * ``datetime-local`` interprets its value as the browser's local time
 * (no timezone). Most code historically used ``d.toISOString().slice(0, 16)``,
 * but that converts the Date to UTC FIRST and then strips the ``Z``, so
 * the widget reads the result back as local — a shift of one offset in
 * either direction. In BST this means 09:00 BST in becomes 08:00 displayed.
 *
 * This helper renders the components directly from the local Date, so
 * the round-trip through the widget is identity in any timezone.
 */
export function toDatetimeLocalString(d) {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Parse a due_at / created_at / completed_at string from the Task API.
 *
 * Two backend shapes are supported:
 *   1. ISO 8601 with proper timezone offset — ``2026-05-20T09:00:00+01:00``
 *      (passed through to dayjs unchanged; dayjs handles the offset).
 *   2. The DRF DATETIME_FORMAT output — ``"20-05-2026 09:00:00.000000Z"``
 *      where the trailing ``Z`` is a LITERAL, not a UTC marker. See the
 *      module docstring above for why. We rewrite the date portion to
 *      ``YYYY-MM-DD``, drop the literal ``Z``, and parse as naive local —
 *      which is correct for UK practice users (browser TZ = Europe/London).
 *
 * @param {string|null} raw - Raw datetime string from the API.
 * @returns {dayjs.Dayjs|null} A dayjs instance, or null if input is falsy.
 */
export function parseApiDate(raw) {
    if (!raw) return null;
    // Already ISO 8601 (starts with a 4-digit year). When the backend emits
    // a real offset (e.g. "+01:00"), dayjs handles it natively.
    if (/^\d{4}-/.test(raw)) return dayjs(raw);
    // DD-MM-YYYY HH:MM:SS[.ffffff][Z] from DRF's DATETIME_FORMAT. The "Z"
    // is a LITERAL character, NOT a UTC marker. Strip it (along with the
    // microsecond fraction, which dayjs's default parser tolerates) and
    // hand a naive ISO 8601 string to dayjs — which interprets it as the
    // browser's local time. For UK practice users on UK browsers this is
    // London local, which matches what the backend emitted.
    const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}:\d{2}:\d{2})(\.\d+)?Z?$/);
    if (match) {
        const [, dd, mm, yyyy, time, frac] = match;
        return dayjs(`${yyyy}-${mm}-${dd}T${time}${frac || ''}`);
    }
    // Fallback — let dayjs try its best.
    return dayjs(raw);
}
